import { useEffect, useRef, useState } from 'react';
import { TEMPLATES } from '@captionflow/template-schema';
import {
  captionStateAt,
  layoutSegment,
  drawCaption,
  type CaptionStyle,
  type CaptionPlacement,
  type CaptionSegment,
  type TranscriptWord,
  type Ctx2D,
} from '@captionflow/caption-engine';
import { browserMeasurer } from '../engine/measurer';
import { useCaptionFontsReady } from '../lib/captionFonts';

/**
 * A 400×400 accurate preview of EVERY caption template, drawn with the same
 * caption engine the live editor + export use (layoutSegment + drawCaption). Each
 * card shows all three text levels at once - plain words, the currently-active
 * (spoken) word, and the emphasized key word - on a modern gradient ground so a
 * style reads the way it would over real footage.
 */

// The sample line: mostly plain words, one emphasized key word ("HOOK"), and the
// last word left as the ACTIVE one at the chosen time, so a single frame shows
// normal + active + emphasis together.
const SAMPLE_WORDS: TranscriptWord[] = [
  { id: 'w0', text: 'This', startMs: 0, endMs: 350 },
  { id: 'w1', text: 'is', startMs: 350, endMs: 650 },
  { id: 'w2', text: 'your', startMs: 650, endMs: 950 },
  { id: 'w3', text: 'HOOK', startMs: 950, endMs: 1500, emphasized: true },
  { id: 'w4', text: 'moment', startMs: 1500, endMs: 2200 },
];
const SAMPLE_SEGMENT: CaptionSegment = {
  id: 'seg',
  wordIds: ['w0', 'w1', 'w2', 'w3', 'w4'],
  startMs: 0,
  endMs: 2200,
};
const SAMPLE_WORD_MAP = new Map(SAMPLE_WORDS.map((w) => [w.id, w]));
// "moment" is the active word here; every earlier word (incl. the emphasized
// HOOK) has been revealed, so the frame shows all text levels at once.
const SAMPLE_TIME_MS = 1850;
const PREVIEW_PX = 400;
// The caption's target on-canvas font, so text fills the 400px card the same way
// regardless of a template's own reference font size.
const TARGET_CAP_PX = 46;

type BgVariant = 'mesh' | 'aurora' | 'sunset' | 'noir' | 'studio';

const BG_OPTIONS: { id: BgVariant; label: string }[] = [
  { id: 'mesh', label: 'Mesh' },
  { id: 'aurora', label: 'Aurora' },
  { id: 'sunset', label: 'Sunset' },
  { id: 'studio', label: 'Studio' },
  { id: 'noir', label: 'Noir' },
];

/** Fill a soft radial "blob" of color — the building block of a gradient mesh. */
function blob(ctx: CanvasRenderingContext2D, x: number, y: number, r: number, color: string) {
  const g = ctx.createRadialGradient(x, y, 0, x, y, r);
  g.addColorStop(0, color);
  g.addColorStop(1, color.replace(/[\d.]+\)$/, '0)'));
  ctx.fillStyle = g;
  ctx.fillRect(0, 0, ctx.canvas.width, ctx.canvas.height);
}

/** A nice, screenshot-worthy ground behind the caption (procedural, no assets). */
function paintBackground(ctx: CanvasRenderingContext2D, size: number, variant: BgVariant) {
  const s = size;
  if (variant === 'noir') {
    ctx.fillStyle = '#111318';
    ctx.fillRect(0, 0, s, s);
    // faint grid
    ctx.strokeStyle = 'rgba(255,255,255,0.04)';
    ctx.lineWidth = Math.max(1, s / 400);
    const step = s / 9;
    for (let i = 1; i < 9; i++) {
      ctx.beginPath(); ctx.moveTo(i * step, 0); ctx.lineTo(i * step, s); ctx.stroke();
      ctx.beginPath(); ctx.moveTo(0, i * step); ctx.lineTo(s, i * step); ctx.stroke();
    }
    const v = ctx.createRadialGradient(s / 2, s / 2, s * 0.2, s / 2, s / 2, s * 0.75);
    v.addColorStop(0, 'rgba(0,0,0,0)');
    v.addColorStop(1, 'rgba(0,0,0,0.55)');
    ctx.fillStyle = v; ctx.fillRect(0, 0, s, s);
    return;
  }
  if (variant === 'studio') {
    // Warm neutral studio backdrop — like a softbox-lit set.
    const g = ctx.createLinearGradient(0, 0, 0, s);
    g.addColorStop(0, '#5b5652'); g.addColorStop(0.5, '#3d3a38'); g.addColorStop(1, '#211f1e');
    ctx.fillStyle = g; ctx.fillRect(0, 0, s, s);
    blob(ctx, s * 0.5, s * 0.28, s * 0.6, 'rgba(255,240,214,0.35)');
    return;
  }
  if (variant === 'sunset') {
    const g = ctx.createLinearGradient(0, 0, s, s);
    g.addColorStop(0, '#2a1650'); g.addColorStop(0.5, '#7b2f6b'); g.addColorStop(1, '#e0663f');
    ctx.fillStyle = g; ctx.fillRect(0, 0, s, s);
    blob(ctx, s * 0.72, s * 0.78, s * 0.5, 'rgba(255,196,120,0.55)');
    blob(ctx, s * 0.2, s * 0.2, s * 0.45, 'rgba(120,90,255,0.35)');
    return;
  }
  if (variant === 'aurora') {
    ctx.fillStyle = '#0a1626';
    ctx.fillRect(0, 0, s, s);
    blob(ctx, s * 0.28, s * 0.35, s * 0.55, 'rgba(52,211,153,0.45)');
    blob(ctx, s * 0.72, s * 0.3, s * 0.5, 'rgba(96,165,250,0.45)');
    blob(ctx, s * 0.55, s * 0.8, s * 0.55, 'rgba(167,139,250,0.42)');
    return;
  }
  // 'mesh' (default): a premium multi-color gradient mesh.
  ctx.fillStyle = '#141026';
  ctx.fillRect(0, 0, s, s);
  blob(ctx, s * 0.22, s * 0.24, s * 0.6, 'rgba(139,92,246,0.55)');
  blob(ctx, s * 0.82, s * 0.28, s * 0.55, 'rgba(236,72,153,0.42)');
  blob(ctx, s * 0.78, s * 0.82, s * 0.6, 'rgba(34,211,238,0.4)');
  blob(ctx, s * 0.2, s * 0.85, s * 0.5, 'rgba(59,130,246,0.4)');
}

function TemplatePreview400({ style, placement, bg }: { style: CaptionStyle; placement: CaptionPlacement; bg: BgVariant }) {
  const ref = useRef<HTMLCanvasElement>(null);
  const fontsReady = useCaptionFontsReady();

  useEffect(() => {
    const canvas = ref.current;
    const ctx = canvas?.getContext('2d');
    if (!canvas || !ctx) return;
    const dpr = Math.min(3, window.devicePixelRatio || 1);
    const px = PREVIEW_PX * dpr;
    canvas.width = px;
    canvas.height = px;

    paintBackground(ctx, px, bg);

    const state = captionStateAt([SAMPLE_SEGMENT], SAMPLE_WORD_MAP, SAMPLE_TIME_MS);
    if (!state.segment || state.words.length === 0) return;

    // The engine scales fonts by frame.height / REFERENCE_HEIGHT (1920). Pick a
    // virtual frame height so the rendered font ≈ TARGET_CAP_PX on our card, and
    // center the caption block. Use the template's own horizontal placement width.
    const targetCap = TARGET_CAP_PX * dpr;
    const frameH = (targetCap / style.fontSizePx) * 1920;
    const previewPlacement: CaptionPlacement = {
      anchor: 'center',
      xNorm: 0.5,
      yNorm: 0.5,
      maxWidthNorm: Math.min(0.86, placement.maxWidthNorm ?? 0.86),
    };
    const layout = layoutSegment(state.words, style, previewPlacement, { width: px, height: frameH }, browserMeasurer);
    ctx.save();
    ctx.translate(0, (px - frameH) / 2); // map the frame's vertical center to the card center
    drawCaption(ctx as unknown as Ctx2D, { style, layout, state, measurer: browserMeasurer });
    ctx.restore();
  }, [style, placement, bg, fontsReady]);

  return <canvas ref={ref} style={{ width: '100%', maxWidth: PREVIEW_PX, aspectRatio: '1 / 1', display: 'block', borderRadius: 12 }} />;
}

export function CaptionPreviewGallery() {
  const [open, setOpen] = useState(true);
  const [bg, setBg] = useState<BgVariant>('mesh');
  return (
    <section style={styles.card}>
      <button style={styles.head} onClick={() => setOpen((v) => !v)}>
        <h3 style={styles.h3}>
          Caption previews · all templates ({TEMPLATES.length})
        </h3>
        <span style={{ color: '#8aa0ff', fontSize: 12 }}>{open ? '▾ hide' : '▸ show'}</span>
      </button>
      {open && (
        <>
          <p style={styles.note}>
            A 400×400 render of every caption style, drawn with the real caption engine. Each shows all
            text levels at once: plain words, the <b style={{ color: '#cfd3e6' }}>active</b> word, and the{' '}
            <b style={{ color: '#cfd3e6' }}>emphasized</b> key word ("HOOK").
          </p>
          <div style={styles.bgbar}>
            <span style={{ color: '#8b8f9c', fontSize: 12 }}>Background:</span>
            {BG_OPTIONS.map((o) => (
              <button
                key={o.id}
                onClick={() => setBg(o.id)}
                style={{ ...styles.chip, ...(bg === o.id ? styles.chipOn : null) }}
              >
                {o.label}
              </button>
            ))}
          </div>
          <div style={styles.grid}>
            {TEMPLATES.map((t) => (
              <div key={t.id} style={styles.cell}>
                <TemplatePreview400 style={t.style} placement={t.placement} bg={bg} />
                <div style={styles.meta}>
                  <strong style={styles.name}>
                    {t.name}
                    {t.ai && <span style={styles.aiBadge}>AI</span>}
                  </strong>
                  <small style={styles.sub}>
                    {t.renderMode === 'behind' ? 'depth · behind subject' : t.category}
                  </small>
                </div>
              </div>
            ))}
          </div>
        </>
      )}
    </section>
  );
}

const styles: Record<string, React.CSSProperties> = {
  card: { background: '#14161d', border: '1px solid #23262f', borderRadius: 14, padding: 16, marginBottom: 16 },
  head: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%', background: 'transparent', border: 'none', cursor: 'pointer', padding: 0 },
  h3: { margin: 0, fontSize: 13, textTransform: 'uppercase', letterSpacing: '0.06em', color: '#8b8f9c' },
  note: { color: '#777', fontSize: 12, lineHeight: 1.6, margin: '10px 0 12px' },
  bgbar: { display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', margin: '0 0 16px' },
  chip: { background: '#0f1117', color: '#aab', border: '1px solid #2a2d38', borderRadius: 999, padding: '5px 12px', fontSize: 12, cursor: 'pointer' },
  chipOn: { background: 'linear-gradient(100deg,#a06bff,#42c6ff)', color: '#fff', borderColor: 'transparent' },
  grid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: 16 },
  cell: { background: '#0f1117', border: '1px solid #23262f', borderRadius: 14, padding: 10, display: 'flex', flexDirection: 'column', gap: 8 },
  meta: { display: 'flex', flexDirection: 'column', gap: 2, padding: '0 2px 2px' },
  name: { display: 'inline-flex', alignItems: 'center', gap: 6, color: '#e7e7ea', fontSize: 13 },
  sub: { color: '#8b8f9c', fontSize: 11.5 },
  aiBadge: { background: 'linear-gradient(100deg,#ff6fae,#a06bff)', color: '#fff', fontSize: 9, fontWeight: 800, padding: '1px 5px', borderRadius: 5, letterSpacing: '0.04em' },
};
